import React, { useContext } from 'react';

import { Card, List } from 'antd';

import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';
import { UserContext } from '@contexts/UserContext';

import BookItem from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/BookItem';

import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import { NwButton } from '@components/Gui/NwButton';
import { NwCard, NwCardContent } from '@components/Gui/NwCard/NwCard';

import FastPackageSortableBooksList from './FastPackageSortableBooksList';
import ModelDetailsBooksCardHeaderActions from './ModelDetailsBooksCardHeaderActions';
import ModelDetailsBooksCharacteristicsFilter from './ModelDetailsBooksCharacteristicsFilter';
import { useModelDetailsBooks } from './ModelDetailsBooksContext';
import ModelDetailsBooksDrawerFilters from './ModelDetailsBooksDrawerFilters';
import ModelDetailsBooksPublicationsSummary from './ModelDetailsBooksPublicationsSummary';
import { useFastPackageOrderManager } from './useFastPackageOrderManager';
import { useModelDetailsBooksList } from './useModelDetailsBooksList';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const GridCard = styled(Card.Grid)`
    float: left; //added because ant removed it to use flex- to update later
    width: 50%;
    height: 170px;
    background: white;
    padding: 12px 24px;
    @media only screen and (max-width: 720px) {
        width: 100%;
    }
`;

const CardWrapper = styled.div`
    height: 100%;
    position: relative;
    .ant-card-body-content {
        padding: 0 !important;
        justify-content: ${(props) => !props.viewModelBooks && 'center !important'};
    }
    .ant-card-bordered {
        border: none !important;
    }

    /* Su mobile i controlli non stanno accanto al titolo: titolo e azioni occupano
       una riga intera ciascuno. La base 100% sul titolo è necessaria perché
       .ant-card-head-title ha overflow hidden e altrimenti collassa a larghezza zero
       invece di mandare a capo l'extra. */
    @media only screen and (max-width: 720px) {
        .ant-card-head-wrapper {
            flex-wrap: wrap;
        }
        .ant-card-head-title {
            flex: 1 0 100%;
        }
        .ant-card-extra {
            flex: 1 0 100%;
            margin-left: 0;
        }
        .nw-card-head-title {
            flex-wrap: wrap;
        }
    }

    ${(props) =>
        props.isBooksDrawer &&
        `
        .ant-card-head .ant-card-head-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
        }
        .ant-card-head .ant-card-extra {
            margin-left: 0;
            margin-right: 0;
            flex: 1;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            min-width: 0;
        }
    `}
`;

const ModelDetailsBooksTitleActions = styled.div`
    display: flex;
    align-items: center;
    gap: 2rem;
`;

const ModelDetailsBooksCard = () => {
    const { contactID, modelPlaylists, actions, readOnly, refreshing, scope, websiteData } = useModelDetailsBooks();
    const { currentUser } = useContext(UserContext);
    const booksDrawer = scope === 'books-drawer';

    //TODO: RIPRISTINARE CONTROLLO  SENZA CARICARE MODELLA
    //const viewModelBooks = useUserPermission('ViewModelBooks', currentContact && currentContact.Users.map(user => user.UserID));

    const layoutContext = useLayoutContext();
    const { isMobile, breakpoints } = layoutContext;

    const shouldPaginate = !booksDrawer && breakpoints !== 1;
    const showTwoColumnsBooksLayout = scope === 'model-details' && breakpoints === 5;
    const {
        activePage,
        booksSort,
        booksToRender,
        displayedBooks,
        filterTags,
        tagFilterMode,
        hasAnyFastPackage,
        onlyMine,
        playlistCharacteristicFilter,
        playlists,
        setActivePage,
        setBooksSort,
        setFilterTags,
        setTagFilterMode,
        setOnlyMine,
        setPlaylistCharacteristicFilter,
    } = useModelDetailsBooksList({
        contactID,
        modelPlaylists,
        scope,
        currentUserId: currentUser.UserID,
        itemsPerPage: 4,
        shouldPaginate,
        websiteData,
        isMobile,
    });
    const {
        displayedFastPackageBooks,
        fastPackageOnly,
        handleFastPackageDragEnd,
        handleSaveFastPackageOrder,
        saveOrderFeedback,
        savingFastPackageOrder,
        setFastPackageOnly,
        setSaveOrderFeedback,
    } = useFastPackageOrderManager({
        playlists,
        contactID,
        enabled: booksDrawer,
        hasAnyFastPackage,
    });

    const booksListToRender = booksDrawer && fastPackageOnly ? displayedFastPackageBooks : displayedBooks;

    const openBooksDrawer = () => {
        actions.onOpenBooksDrawer();
    };

    const titleAction =
        scope === 'model-details' || scope === 'model-drawer' ? (
            <ModelDetailsBooksTitleActions>
                {/* Il books drawer riordina i fast package in drag&drop: non adatto al tatto */}
                {!isMobile && (
                    <NwButton
                        minimal
                        icon={byPrefixAndName.fass['expand-wide']}
                        solid
                        size="small"
                        onClick={openBooksDrawer}
                    />
                )}
                <ModelDetailsBooksCharacteristicsFilter
                    value={playlistCharacteristicFilter}
                    onChange={setPlaylistCharacteristicFilter}
                />
            </ModelDetailsBooksTitleActions>
        ) : null;

    return playlists && !refreshing ? (
        <>
            <CardWrapper className="books-card-wrapper" viewModelBooks isBooksDrawer={booksDrawer}>
                <NwCard
                    title={scope === 'model-details' || scope === 'model-drawer' ? 'Books' : ''}
                    titleAction={titleAction}
                    counter={scope === 'model-details' ? playlists.length : null}
                    icon={!booksDrawer && breakpoints !== 1 ? byPrefixAndName.fasl['images'] : null}
                    fullheight
                    extra={
                        <ModelDetailsBooksCardHeaderActions
                            booksDrawer={booksDrawer}
                            booksSort={booksSort}
                            fastPackageOnly={fastPackageOnly}
                            hasAnyFastPackage={hasAnyFastPackage}
                            isMobile={isMobile}
                            onBooksSortChange={setBooksSort}
                            onOpenAddBookDrawer={actions.onOpenAddBookDrawer}
                            onToggleFastPackageOnly={() => setFastPackageOnly((value) => !value)}
                            scope={scope}
                        />
                    }
                >
                    {booksDrawer && !fastPackageOnly && (
                        <ModelDetailsBooksDrawerFilters
                            booksSort={booksSort}
                            filterTags={filterTags}
                            tagFilterMode={tagFilterMode}
                            onBooksSortChange={setBooksSort}
                            onFilterTagsChange={(name, selectedTags) => setFilterTags(selectedTags)}
                            onTagFilterModeChange={setTagFilterMode}
                            onOnlyMineChange={setOnlyMine}
                            onPlaylistCharacteristicFilterChange={setPlaylistCharacteristicFilter}
                            onlyMine={onlyMine}
                            playlistCharacteristicFilter={playlistCharacteristicFilter}
                        />
                    )}
                    {playlists.length > 0 ? (
                        <NwCardContent
                            pagination={
                                !shouldPaginate
                                    ? null
                                    : {
                                          total: booksListToRender.length,
                                          current: activePage,
                                          pageSize: 4,
                                          onChange: setActivePage,
                                      }
                            }
                            padding="no"
                        >
                            <ModelDetailsBooksPublicationsSummary
                                playlists={playlists}
                                readOnly={readOnly}
                                websiteData={websiteData}
                            />
                            {booksListToRender && booksListToRender.length > 0 ? (
                                booksDrawer ? (
                                    fastPackageOnly ? (
                                        <FastPackageSortableBooksList
                                            books={booksListToRender}
                                            onCloseFeedback={() => setSaveOrderFeedback(null)}
                                            onDragEnd={handleFastPackageDragEnd}
                                            onSave={handleSaveFastPackageOrder}
                                            saveOrderFeedback={saveOrderFeedback}
                                            savingFastPackageOrder={savingFastPackageOrder}
                                        />
                                    ) : (
                                        booksListToRender.map((book, index) => (
                                            <GridCard key={index}>
                                                <div>
                                                    <BookItem book={book} />
                                                </div>
                                            </GridCard>
                                        ))
                                    )
                                ) : showTwoColumnsBooksLayout ? (
                                    booksToRender.map((book, index) => (
                                        <GridCard key={index}>
                                            <div>
                                                <BookItem book={book} />
                                            </div>
                                        </GridCard>
                                    ))
                                ) : (
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={booksToRender}
                                        renderItem={(book) => {
                                            return <BookItem book={book} />;
                                        }}
                                    />
                                )
                            ) : (
                                <EmptyState message="no books" />
                            )}
                        </NwCardContent>
                    ) : (
                        <EmptyState message="no books" />
                    )}
                </NwCard>
            </CardWrapper>
        </>
    ) : (
        <Loading textBlack />
    );
};

export default ModelDetailsBooksCard;
